import React from 'react'
import { useIntl } from 'react-intl'
import { Column } from 'primereact/column'
import { DataTableFun } from '../../../utils/helpers/dataTableFun'
import useModal from '../../../utils/hooks/useModal'
import Modal from '../../../components/modal/Modal'
import PopulationsForm from '../../../components/settings/PopulationsForm'
import useAccess from '../../../utils/hooks/useAccess'
import { GROUPS, MODULES } from '../../../utils/constants/roles'
import useInputs from '../../../utils/hooks/useInputs'
import PopulationsService from '../../../services/settings/populations/populations.service'
import useHandlerBasicActions from '../../../utils/hooks/useHandlerBasicActions'
import DataTableComp from '../../../components/common/data-table/DataTableComp'
import MainCommonView from '../../../components/common/MainCommonView'
import { formatNumber, formatUnits } from '../../../utils/helpers/common'

const Populations = () => {
  const { formatMessage: msg } = useIntl()

  const [modalTitle, setModalTitle] = React.useState(
    msg({ id: 'populations.add.title' }),
  )

  const { isShowing, toggle } = useModal()
  const { canAdd, canDelete, canEdit } = useAccess()
  const { inputs, updateInputs, onChangeInputs } = useInputs()

  const dataFacade = useHandlerBasicActions({
    sortFieldInitial: 'name',
    getAll: PopulationsService.getAll,
    removeAction: PopulationsService.remove,
    editAction: PopulationsService.update,
    createAction: PopulationsService.create,
  })

  const handleCreate = async () => {
    let resp = null
    if (!inputs?._id) {
      resp = await PopulationsService.create(inputs)
    } else {
      resp = await PopulationsService.update({ _id: inputs?._id, body: inputs })
    }
    if (resp) {
      closeModal()
      dataFacade.reload()
    }
  }

  const onRowEditComplete = async ({ newData }) => {
    const body = {
      name: newData.name,
      province: newData.province,
      country: newData.country,
      kmToVic: newData.kmToVic,
    }
    const { _id } = newData
    await dataFacade.edit({ _id, body })
  }

  const closeModal = () => {
    updateInputs({})
    toggle()
  }

  const headerOptions = {
    title: msg({ id: 'populations' }),
    handlerHeaderActions: {
      showBackButton: true,
      secondaryButton: canAdd(MODULES.SETTINGS, GROUPS.BILLABLES) && toggle,
      secondaryButtonText: 'button.add',
      secondaryButtonIcon: 'pi-plus',
    },
    searchOptions: dataFacade?.searchOptions,
  }

  const pagerOptions = {
    ...dataFacade?.pagerOptions,
    totalRecords: dataFacade?.data?.total,
  }

  const dataTable = (
    <DataTableComp
      loading={dataFacade?.loading}
      tableOptions={{
        className: 'table-without-menu',
        value: dataFacade?.data?.resp,
        dataKey: '_id',
        editMode: 'row',
        selectionMode: 'single',
        onRowEditComplete,
        onRowSelect: (e) => {
          if (canEdit) {
            updateInputs(e.data)
            setModalTitle(msg({ id: 'populations.edit.title' }))
            toggle()
          }
        },
        ...dataFacade?.sortOptions,
      }}
      columns={[
        <Column
          style={{ width: '20%' }}
          field="name"
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          header={msg({ id: 'name' })}
          sortable
        />,
        <Column
          style={{ width: '30%' }}
          field="province"
          sortable
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'province' })}
        />,
        <Column
          style={{ width: '30%' }}
          field="country"
          sortable
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'country' })}
        />,
        <Column
          style={{ width: '20%' }}
          field="kmToVic"
          align="right"
          body={(rowData) => formatUnits(rowData.kmToVic).concat(' Km')}
          sortable
          editor={(rowData) => DataTableFun.textEditor(rowData)}
          onCellEditComplete={DataTableFun.onCellEditComplete}
          header={msg({ id: 'kmToVic' })}
        />,

        canDelete && (
          <Column
            style={{ paddingLeft: '80px' }}
            className="row-delete"
            align="right"
            body={(rowData) =>
              DataTableFun.buttonDelete(rowData, dataFacade.remove)
            }
          />
        ),
        //canEdit && <Column className="row-edit" align="right" rowEditor />,
      ]}
    />
  )

  return (
    <div className="roles-container">
      <MainCommonView
        headerOptions={headerOptions}
        componentData={dataTable}
        pagerOptions={pagerOptions}
      />

      <Modal
        isShowing={isShowing}
        hide={closeModal}
        width="90%"
        maxWidth="450px"
        title={modalTitle}
        context={
          <PopulationsForm
            inputs={inputs}
            handleSubmit={handleCreate}
            handleChange={onChangeInputs}
          />
        }
      />
    </div>
  )
}

export default Populations
